import { PageHeader, PageShell } from '@/components/PageShell';
import { AddTestConsiderationButton } from '@/app/homes/considerations/AddTestConsiderationButton';
import { HomeConsiderationsTab } from '@/app/homes/considerations/HomeConsiderationsTab';
import { HomeConsiderationTypeDetailsPage } from '@/app/homes/considerations/HomeConsiderationTypeDetailsPage';
import { HomeChatsTab } from '@/app/homes/chats/HomeChatsTab';
import { HomeDocumentTab } from '@/app/homes/document/HomeDocumentTab';
import { HomeEquipmentTab } from '@/app/homes/HomeEquipmentTab';
import { HomeOverviewTab } from '@/app/homes/HomeOverviewTab';
import { HomeRemindersTab } from '@/app/homes/reminders/HomeRemindersTab';
import SendTestNotificationButton from '@/app/homes/SendTestNotificationButton';
import { TaskDetailsPage } from '@/app/homes/tasks/TaskDetailsPage';
import { HomeTasksTab } from '@/app/homes/tasks/HomeTasksTab';
import { useToggleHomeDeleteAction } from '@/app/homes/useToggleHomeDeleteAction';
import { TabNav } from '@/components/TabNav';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { currentHomeIdState, currentHomeState, spacesState } from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import type { HomeId } from '@rock/types';
import { useSetAtom } from 'jotai';
import { RotateCcw, Trash2 } from 'lucide-react';
import type { FC } from 'react';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

export const HomeDetailsPage: FC = () => {
    const { homeId = '' as HomeId } = useParams<{ homeId: HomeId }>();
    const setCurrentHomeId = useSetAtom(currentHomeIdState);
    const [home, homeLoadable] = useLoadableAtomValue(currentHomeState);
    const spaces = useAsyncAtomValue(spacesState, []);
    const { setConfirmHomeToggle, ToggleHomeDeleteDialog } = useToggleHomeDeleteAction(homeId);

    useEffect(() => {
        if (homeId) {
            setCurrentHomeId(homeId as HomeId);
        }
        return () => {
            setCurrentHomeId(undefined);
        };
    }, [homeId, setCurrentHomeId]);

    // currentHomeState re-targets when currentHomeIdState changes; until our effect runs it may
    // still hold the PREVIOUS home (or undefined) — gate on both id match and loadable state.
    if (!home || home.id !== homeId) {
        const isLoading = homeLoadable.state === 'loading' || !!(home && home.id !== homeId);
        return (
            <PageShell width="narrow">
                <Card>
                    <CardHeader>
                        <CardTitle>{isLoading ? 'Loading home details' : 'Home not found'}</CardTitle>
                        <CardDescription>
                            {isLoading
                                ? 'We are fetching the latest information for this home.'
                                : `No home exists with id ${homeId}.`}
                        </CardDescription>
                    </CardHeader>
                </Card>
            </PageShell>
        );
    }

    return (
        <PageShell>
            <PageHeader title={home.name} id={home.id} backLabel="Back to homes">
                <AddTestConsiderationButton homeId={home.id} />
                <SendTestNotificationButton userId={home.owner} homeId={home.id} />
                {home.active ? (
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setConfirmHomeToggle(true)}
                        aria-label="Delete home"
                    >
                        <Trash2 className="h-4 w-4" />
                        Delete
                    </Button>
                ) : (
                    <Button
                        variant="default"
                        size="sm"
                        onClick={() => setConfirmHomeToggle(true)}
                        aria-label="Restore home"
                    >
                        <RotateCcw className="h-4 w-4" />
                        Restore
                    </Button>
                )}
            </PageHeader>

            <TabNav
                tabs={[
                    { label: 'Overview', to: `/directory/homes/${homeId}/overview` },
                    { label: 'Equipment', to: `/directory/homes/${homeId}/equipment` },
                    { label: 'Reminders', to: `/directory/homes/${homeId}/reminders` },
                    { label: 'Tasks', to: `/directory/homes/${homeId}/tasks` },
                    { label: 'Considerations', to: `/directory/homes/${homeId}/considerations` },
                    { label: 'Chats', to: `/directory/homes/${homeId}/chats` },
                    { label: 'Home Document', to: `/directory/homes/${homeId}/document` },
                ]}
            />

            <Routes>
                <Route index element={<Navigate replace to="overview" />} />
                <Route path="overview" element={<HomeOverviewTab home={home} />} />
                <Route path="equipment" element={<HomeEquipmentTab home={home} spaces={spaces} />} />
                <Route path="reminders" element={<HomeRemindersTab homeId={homeId} />} />
                <Route path="tasks" element={<HomeTasksTab homeId={homeId} />} />
                <Route path="tasks/:taskId" element={<TaskDetailsPage />} />
                <Route path="considerations/*" element={<HomeConsiderationsRoute homeId={homeId} />} />
                <Route path="chats" element={<HomeChatsTab homeId={homeId} ownerId={home.owner} />} />
                <Route path="document" element={<HomeDocumentTab />} />
            </Routes>
            <ToggleHomeDeleteDialog />
        </PageShell>
    );
};

const HomeConsiderationsRoute: FC<{ homeId: HomeId }> = ({ homeId }) => {
    return (
        <Routes>
            <Route index element={<HomeConsiderationsTab homeId={homeId} />} />
            <Route path=":typeId/*" element={<HomeConsiderationTypeDetailsPage />} />
        </Routes>
    );
};
